import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight i18n. No dependency — a flat key/string map per language.
//
// German is the default: the primary audience is the customer's team.
// Keys are dotted and grouped by area. A missing key falls back to German,
// then to the key itself, so an untranslated string is visible rather than
// silently blank.
// ─────────────────────────────────────────────────────────────────────────────

export type Lang = 'de' | 'en' | 'fr';

export const LANGUAGES: Array<{ code: Lang; label: string; locale: string }> = [
  { code: 'de', label: 'DE', locale: 'de-DE' },
  { code: 'en', label: 'EN', locale: 'en-GB' },
  { code: 'fr', label: 'FR', locale: 'fr-FR' },
];

const STORAGE_KEY = 'oclab.lang';

type Dict = Record<string, string>;

const de: Dict = {
  // Chrome
  'app.signOut': 'Abmelden',
  'app.allDemos': 'Alle',
  'app.language': 'Sprache',
  'app.back': 'Zurück',

  // Landing
  'landing.eyebrow': 'Prototypen-Labor',
  'landing.title': 'Türintelligenz',
  'landing.subtitle': 'Prototyp-Demos',
  'landing.intro': 'Funktionsfähige Demonstratoren, die zeigen, was Türsysteme aussagen können, sobald ihre Daten erfasst, aggregiert und modelliert werden. Jeder Prototyp arbeitet mit repräsentativen Daten — wählen Sie einen aus.',
  'landing.demonstrators': 'Demonstratoren',
  'landing.open': 'Demo öffnen',
  'landing.comingSoon': 'In Vorbereitung',
  'landing.footerNote': 'Repräsentative Daten — nicht aus dem Fahrgastbetrieb',
  'landing.env': 'OPEN CAPACITY · LABORUMGEBUNG',

  // Login
  'login.eyebrow': 'Türintelligenz',
  'login.title': 'Anmelden',
  'login.subtitle': 'Der Zugang zum Prototypen-Labor erfolgt auf Einladung.',
  'login.email': 'E-Mail',
  'login.password': 'Passwort',
  'login.submit': 'Anmelden',
  'login.error': 'E-Mail-Adresse oder Passwort ungültig.',

  // Fleet menu
  'fleet.reportingDate': 'Auswertungsdatum',
  'fleet.asOf': 'Stand',
  'fleet.local': 'Ortszeit',
  'fleet.title': 'Flotte',
  'fleet.withData': 'von {total} mit Daten',
  'fleet.filter': 'Einheiten filtern…',
  'fleet.noData': 'keine Daten',
  'fleet.ready': 'Mit Daten',
  'fleet.remaining': 'Übrige Flotte',
  'fleet.noMatch': 'Keine Einheit passt zu „{q}“.',
  'fleet.footnote': 'Übrige Einheiten sind zur Veranschaulichung des Flottenumfangs gelistet. Türdaten liegen in diesem Build nur für {units} vor.',
  'fleet.collapse': 'Flottenmenü ausblenden',
  'fleet.expand': 'Flottenmenü einblenden',
  'fleet.unit': 'Einheit',

  // Prototype titles
  'proto.01.short': 'Betrieb',
  'proto.01.title': 'Türbetrieb — Dashboard',
  'proto.01.tag': 'Flottenübersicht',
  'proto.01.desc': 'Wärmekarte der Türnutzung eines vierteiligen Triebzugs. Zyklen, Missbrauchsereignisse, Wartungsprognose und Zustandsindikatoren je Tür.',
  'proto.02.short': 'Halteaktivität',
  'proto.02.title': 'Halteaktivitätsanalyse',
  'proto.02.tag': 'Fahrgastfluss',
  'proto.02.desc': 'Türintelligenz auf Fahrtebene. Türaktivität je Halt, Bahnsteig- gegen Gleisseite, Verteilung vorn/hinten und Missbrauchsmarkierungen.',
  'proto.03.short': 'Diagnose',
  'proto.03.title': 'Türknoten-Diagnose',
  'proto.03.tag': 'Diagnose',
  'proto.03.desc': 'Knotendiagnose für Instandhalter. Motorstromverläufe, Positionssignale und Zyklenzahlen über alle 16 Türknoten.',
  'proto.04.short': 'Anomalien',
  'proto.04.title': 'CBM-Anomaliebewertung',
  'proto.04.tag': 'Prädiktiv',
  'proto.04.desc': 'Ensemble-Anomalieradar je Zyklus und Flotten-Wärmekarte über acht Wochen. Methodenübereinstimmung von Isolation Forest, LOF, SVM und Autoencoder.',

  // Shared
  'common.syntheticData': 'Synthetische Daten',
  'common.syntheticNote': 'Synthetische Flotte — Einheiten, Haltestellen, Zeiten und Kennungen sind fiktiv',
  'common.confidential': '© 2026 Opencapacity · Vertraulich · Nur für den internen Gebrauch',
  'common.door': 'Tür',
  'common.doors': 'Türen',
  'common.health': 'Zustand',
  'common.cycles': 'Zyklen',
  'common.platform': 'Bahnsteigseite',
  'common.trackside': 'Gleisseite',
  'common.forward': 'vorn',
  'common.rear': 'hinten',
  'common.good': 'Gut',
  'common.monitor': 'Beobachten',
  'common.action': 'Handeln',
  'common.nominal': 'Normal',
  'common.serviceDue': 'Wartung fällig',
  'common.flagged': 'markiert',
  'common.days': 'T',

  // Prototype 01
  'p1.fleetOverview': 'Flottenübersicht',
  'p1.alerts': '{n} Warnungen',
  'p1.draft': 'Konzeptentwurf',
  'p1.cyclesToday': 'Zyklen heute',
  'p1.vsAverage': 'gegenüber Durchschnitt',
  'p1.misuseEvents': 'Missbrauchsereignisse',
  'p1.highSeverity': '{n} mit hoher Schwere',
  'p1.avgCycleTime': 'Ø Zykluszeit',
  'p1.withinRange': 'im Normbereich',
  'p1.nextService': 'Nächste Wartung',
  'p1.flaggedDoor': '{door} markiert',
  'p1.schematic': 'Zugverband · Türnutzung von oben · vierteiliger Triebzug',
  'p1.recentEvents': 'Aktuelle Ereignisse',
  'p1.today': 'Heute',
  'p1.maintenanceForecast': 'Wartungsprognose',
  'p1.horizon': '30-Tage-Horizont',
  'p1.heat.1': 'Gering',
  'p1.heat.2': 'Mäßig',
  'p1.heat.3': 'Hoch',
  'p1.heat.4': 'Sehr hoch',
  'p1.heat.5': 'Spitze',
  'p1.misuseEvent': 'Missbrauchsereignis',
  'p1.sideA': '◀ Seite A · Bahnsteigseite',
  'p1.sideB': 'Seite B · Gleisseite ▶',
  'p1.usage': 'Nutzung',
  'p1.others': 'Übrige',
  'p1.serviceWithin': 'Wartung innerhalb von 7 Tagen empfohlen',
  'p1.approachingThreshold': 'Nähert sich der Verschleißgrenze',
  'p1.slowClose': 'Verlängerte Schließzeit festgestellt',
  'p1.normalWear': 'Normaler Verschleiß',
  'p1.withinExpected': 'Alle im erwarteten Bereich',
  'event.forcedEntry': 'Gewaltsames Öffnen — Tür {door} über das Schließsignal hinaus offen gehalten',
  'event.slowClose': 'Verlängerte Schließzeit — Tür {door}: 6,8 s (Grenzwert 5 s)',
  'event.cycleMilestone': 'Zyklus-Meilenstein — Tür {door} hat 10.000 Zyklen erreicht',

  // Prototype 02
  'p2.title': 'Halteaktivität & Türintelligenz',
  'p2.trip': 'Fahrt',
  'p2.trip.morningPeak': 'Morgenspitze',
  'p2.trip.midday': 'Mittag',
  'p2.trip.eveningPeak': 'Abendspitze',
  'p2.trip.lateEvening': 'Später Abend',
  'p2.overview': 'Fahrtübersicht — Türöffnungen je Halt',
  'p2.selectStop': 'Halt auswählen',
  'p2.stopOf': 'Halt {i} von {n}',
  'p2.arr': 'an',
  'p2.totalOpenings': 'Öffnungen gesamt',
  'p2.activeDoors': 'Aktive Türen',
  'p2.dwellTime': 'Haltezeit',
  'p2.forcedEvents': 'Gewaltsame Öffnungen',
  'p2.heatmapTitle': 'Anzahl Türöffnungen — Ansicht von oben',
  'p2.perDoor': 'Detail je Tür · Spaltenkopf zum Sortieren anklicken',
  'p2.openings': 'Öffnungen',
  'p2.totalOpenTime': 'Gesamtöffnungszeit',
  'p2.flags': 'Markierungen',
  'p2.avg': 'Ø',
  'p2.differential': 'Differenzierte Türnutzung · Bahnsteigseite',
  'p2.forwardPct': 'Vorn (×1)',
  'p2.rearPct': 'Hinten (×2)',
  'p2.unevenNote': 'Eine ungleiche Verteilung deutet auf eine Konzentration des Fahrgastwechsels an einem Wagenende hin.',
  'p2.activityByWagen': 'Aktivität je Wagen',
  'p2.busiestDoor': 'Meistgenutzte Tür',
  'p2.fwdRear': 'Vorn / hinten',
  'p2.positionSplit': 'Positionsverteilung',
  'p2.doorSpread': 'Streuung',
  'p2.maxVsMin': 'max. gegen min. aktiv',
  'p2.noActivity': 'Keine Aktivität',
  'p2.healthPanel': 'Türzustand & Missbrauchsmarkierungen',
  'p2.allNominal': 'Alle Türen an diesem Halt unauffällig',
  'p2.requiresAttention': 'Erfordert Aufmerksamkeit',
  'p2.unitHealth': 'Zustandsübersicht der Einheit',
  'p2.sideL': '◀ Seite L · Bahnsteigseite',
  'p2.sideR': 'Seite R · Gleisseite ▶',
  'p2.stops': 'Halte',

  // Prototype 03
  'p3.system': 'Türsystem des Triebzugs',
  'p3.title': 'Türknoten-Diagnose',
  'p3.nodesOnline': '16 Knoten online',
  'p3.nodeSelector': 'Knotenauswahl — 16 Türen, 4 Wagen',
  'p3.monitorCondition': 'Beobachten',
  'p3.lowUse': 'Geringe Nutzung',
  'p3.normal': 'Normal',
  'p3.high': 'Hoch',
  'p3.critical': 'Kritisch',
  'p3.waveforms': 'Sensorsignale',
  'p3.analysing': 'ANALYSE LÄUFT',
  'p3.current': 'Strom (mA)',
  'p3.position': 'Position (8 Bit)',
  'p3.cycleDistribution': 'Zyklenverteilung',
  'p3.selectedNode': 'Ausgewählter Knoten',
  'p3.standby': 'Knoten bereit',
  'p3.standbyText': 'Wählen Sie einen Türknoten im Schema oder in der Schnellauswahl, um die Diagnose zu starten und die Sensorsignale anzuzeigen.',
  'p3.quickSelect': 'Schnellauswahl',
  'p3.peakCurrent': 'Spitzenstrom Motor',
  'p3.aboveCeiling': 'Oberhalb der erwarteten Grenze von {limit} mA.',
  'p3.ipAddress': 'IP-Adresse',
  'p3.doorId': 'Tür-ID',
  'p3.wagen': 'Wagen',
  'p3.side': 'Seite',
  'p3.selectPrompt': 'Türknoten auswählen, um die Diagnose zu starten',
  'p3.simulated': 'simulierte Signalverläufe',

  // Prototype 04
  'p4.approach': 'Ansatz 2A · Zyklusbewertung → Flotten-Wärmekarte',
  'p4.title': 'Anomaliebewertung je Zyklus & Flottensicht',
  'p4.weeks': '8 Wochen',
  'p4.step2': 'Schritt 2 — Türdetail',
  'p4.peakWeek': 'Spitzenwoche',
  'p4.avgAnomaly': 'Ø Anomalie',
  'p4.eightWeekAvg': 'Achtwochenmittel',
  'p4.cyclesShown': 'Gezeigte Zyklen',
  'p4.representative': 'repräsentativ',
  'p4.selectCycle': 'Zyklus zur Betrachtung im Radar auswählen',
  'p4.step1': 'Schritt 1 — Bewertung je Zyklus',
  'p4.radarIntro': 'Jede Achse steht für ein Erkennungsverfahren. Ein großes, symmetrisches Polygon bedeutet hohe Übereinstimmung der Verfahren; ein unsymmetrisches bedeutet Uneinigkeit, was für sich genommen untersuchenswert ist.',
  'p4.ensemble': 'Ensemble',
  'p4.review': 'Prüfen',
  'p4.inspect': 'Untersuchen',
  'p4.step3': 'Schritt 3 — Flotten-Wärmekarte',
  'p4.heatmapIntro': 'Aggregiertes Ergebnis der Bewertung je Zyklus. Auffällige Zeilen markieren Türen für die Inspektion — Zeile anklicken, um in die Tür einzusteigen.',
  'p4.pipeline': 'Funktionsweise — Bewertungskette',
  'p4.step': 'Schritt',
  'p4.normalClose': 'Normales Schließen',
  'p4.obstructed': 'Blockiert',
  'p4.multiAttempt': 'Mehrfachversuch',
  'p4.openOnly': 'Nur Öffnen',
  'p4.legend.normal': '<10 % normal',
  'p4.legend.low': '10–20 % gering',
  'p4.legend.elevated': '20–35 % erhöht',
  'p4.legend.high': '35–55 % hoch',
  'p4.legend.critical': '>55 % kritisch',
  'p4.pipe1.title': 'Merkmalsextraktion in drei Segmenten',
  'p4.pipe1.body': 'Jeder Türzyklus wird in Beschleunigung, Konstantfahrt und Verzögerung geteilt. Stromverlauf, Energie und Dauer werden je Segment extrahiert.',
  'p4.pipe2.title': 'Vier unabhängige Bewerter',
  'p4.pipe2.body': 'Isolation Forest, LOF, One-Class SVM und Autoencoder bewerten jeden Zyklus unabhängig. Kein Verfahren dominiert das Ergebnis.',
  'p4.pipe3.title': 'Ensemble über Rangmittelung',
  'p4.pipe3.body': 'Die Bewertungen werden in Ränge überführt und gemittelt. Der Ensemble-Wert von 0 bis 100 spiegelt die Übereinstimmung der Verfahren wider.',
  'p4.pipe4.title': 'Aggregation auf Flottenebene',
  'p4.pipe4.body': 'Der Anteil auffälliger Zyklen je Tür und Woche macht Verschleißtrends sichtbar. Auffällige Zeilen markieren Kandidaten für die Instandhaltung vor dem Ausfall.',

  // Prototype 05 — maintenance & action recommendations
  'proto.05.short': 'Wartung',
  'proto.05.title': 'Wartungs- & Handlungsempfehlung',
  'proto.05.tag': 'Instandhaltung',
  'proto.05.desc': 'Zustandstrend je Tür mit Prognoseband, Zyklen über frei wählbare Zeiträume, und daraus abgeleitete Handlungsempfehlungen samt Zeitfenster, Checkliste und Auftrag.',
  'p5.approach': 'Instandhaltung',
  'p5.title': 'Wartungs- & Handlungsempfehlung',
  'p5.illustrative': 'Illustrativ',
  'p5.paletteProvisional': 'Farbwelt vorläufig',
  'p5.scope': 'Die Statistik liefert den Zustandstrend — deterministisch, nachvollziehbar, jederzeit reproduzierbar. Sie sagt keinen Ausfalltermin voraus: das Prognoseband wird mit dem Horizont breiter, die Richtung bleibt belastbar. Die Empfehlung setzt darauf auf und ist bewusst opportunistisch — sie bündelt die Arbeit in ein Zeitfenster, in dem die Einheit ohnehin im Depot steht.',
  'p5.triage.flags': 'Statistische Auffälligkeiten',
  'p5.triage.flags.sub': 'in dieser Woche erkannt',
  'p5.triage.actionable': 'Handlungsrelevant',
  'p5.triage.actionable.sub': 'vom Agenten priorisiert',
  'p5.triage.suppressed': 'Unterdrückt',
  'p5.triage.suppressed.sub': 'als bekannt unkritisch eingestuft',
  'p5.det.label': 'Deterministisch',
  'p5.det.title': 'Zyklen & Zustand je Tür',
  'p5.det.intro': 'Reine Arithmetik auf Zähl- und Stromdaten. Kein Modellurteil, kein Sprachmodell — dieselbe Eingabe ergibt immer dieselbe Ausgabe.',
  'period.label': 'Zeitraum',
  'period.day': 'Pro Tag',
  'period.week': 'Pro Woche',
  'period.month': 'Pro Monat',
  'period.range': 'Von–bis',
  'period.from': 'von',
  'period.to': 'bis',
  'period.cycles': 'Zyklen',
  'p3.representative': 'Repräsentativer Zyklus · aktueller Zustand',
  'p3.bandNote': 'Referenzband aus {n} weiteren Zyklen dieser Tür im Zeitraum ({period}).',
  'p3.vsOwnBand': 'Gegen das eigene Band',
  'p3.insideBand': 'Innerhalb der eigenen Bandbreite',
  'p3.bandHigh': 'Am oberen Rand der eigenen Streuung',
  'p3.bandLow': 'Am unteren Rand der eigenen Streuung',
  'p3.bandPercentile': '{pct}. Perzentil von {n} Referenzzyklen',
  'p3.bandSelfOnly': 'Vergleich nur gegen diese Tür — nicht gegen andere Türen.',
  'p3.cyclesLifetime': 'Zyklen gesamt',
  'p3.cyclesPeriod': 'Zyklen im Zeitraum',
  'period.note': 'Türwerte und Summe beziehen sich auf den gewählten Zeitraum ({days} Tage).',
  'p5.table.door': 'Tür',
  'p5.table.cycles': 'Zyklen',
  'p5.table.current': 'Spitzenstrom',
  'p5.table.health': 'Zustand',
  'p5.table.note': 'Zyklen summiert über den gewählten Zeitraum ({period}). Sortiert nach Zustand, schlechteste Tür zuerst.',
  'p5.trend.title': 'Zustandstrend & Prognoseband',
  'p5.trend.observed': 'Gemessen',
  'p5.trend.projected': 'Projiziert',
  'p5.trend.band': 'Prognoseband',
  'p5.trend.mid': 'Mittelwert',
  'p5.trend.advisory': 'Prüfschwelle',
  'p5.trend.today': 'heute',
  'p5.trend.daysOut': 'Tage',
  'p5.trend.rate': 'Veränderungsrate',
  'p5.trend.rateSub': 'A pro Woche',
  'p5.trend.spread': 'Bandbreite',
  'p5.trend.spreadSub': 'A bei +{days} Tagen',
  'p5.trend.advisoryIn': 'Prüfschwelle in',
  'p5.trend.withinHorizon': 'innerhalb des Horizonts',
  'p5.trend.beyondHorizon': 'jenseits des Horizonts',
  'p5.trend.caveat': 'Das Band wird mit dem Horizont breiter — genau das ist die Aussage. Es benennt, bis wohin der Verlauf belastbar ist, nicht wann die Tür ausfällt.',
  'p5.agent.label': 'Orchestrierung',
  'p5.agent.title': 'Handlungsempfehlung (KI-Agent)',
  'p5.agent.intro': 'Der Agent verbessert die Statistik nicht — er führt zusammen. Er verbindet Trend und Prognose mit Wartungshistorie, Freitextnotizen der letzten Inspektion, Depotbelegung und Teileverfügbarkeit und erzeugt daraus Empfehlung, Zeitfenster, Checkliste und Auftrag.',
  'p5.kind.bundle': 'Bündelung',
  'p5.kind.verify': 'Wirksamkeitsprüfung',
  'p5.kind.external': 'Externer Einfluss',
  'p5.prio.high': 'Hoch',
  'p5.prio.medium': 'Mittel',
  'p5.prio.low': 'Niedrig',
  'p5.rec.evidence': 'Herangezogene Belege',
  'p5.rec.why': 'Warum diese Empfehlung?',
  'p5.rec.hideWhy': 'Begründung ausblenden',
  'p5.rec.slot': 'Vorgeschlagenes Zeitfenster',
  'p5.rec.order': 'Auftrag',
  'p5.rec.checklist': 'Checkliste',
  'p5.rec.minutes': 'Min.',
  'p5.rec.bundle.title': 'Tür {door} in den bereits geplanten Depotaufenthalt am {date} aufnehmen.',
  'p5.rec.verify.title': 'Instandsetzung an Tür {door} hat nicht gehalten — Trend nach {days} Tagen erneut steigend.',
  'p5.rec.external.title': 'Verschleiß an Tür {door} folgt dem Einsatzprofil, nicht der Tür.',
  'p5.ev.rate': 'Rate',
  'p5.ev.current': 'Spitzenstrom',
  'p5.ev.notes': 'Inspektionsnotiz',
  'p5.ev.slot': 'Depotfenster',
  'p5.ev.parts': 'Ersatzteil',
  'p5.ev.intervention': 'Letzte Maßnahme',
  'p5.ev.postRepair': 'Nach Instandsetzung',
  'p5.ev.correlation': 'Korrelation',
  'p5.ev.duty': 'Einsatzprofil',
  'p5.ev.peers': 'Vergleichsflotte',
  'p5.src.stats': 'Statistik',
  'p5.src.notes': 'Inspektion',
  'p5.src.depot': 'Depotplan',
  'p5.src.cmms': 'IH-System',
  'p5.src.timetable': 'Umlaufplan',
  'p5.note.roller': 'Läufer leicht rau, belassen',
  'p5.parts.inStock': 'am Lager',
  'p5.post.resumed': 'Anstieg nach 11 Tagen erneut',
  'p5.corr.dwell': 'Haltezeit & Einstiegszahl',
  'p5.duty.peak': 'Hauptverkehrszeit, langer Halt',
  'p5.peers.sameRoute': '3 Türen gleicher Linie ebenso',
  'p5.why.bundle.1': 'Tür {door} steigt seit Wochen gleichmäßig um {rate} A pro Woche — ein Trend, kein Einzelausreißer.',
  'p5.why.bundle.2': 'Die Notiz der letzten Inspektion beschreibt rauen Lauf, ohne dass eine Maßnahme erfolgt ist. Das deckt sich mit dem gemessenen Verlauf.',
  'p5.why.bundle.3': 'Die Einheit steht am {date} ohnehin im Depot {depot}. Das Zeitfenster lässt sich erweitern, ohne einen zusätzlichen Ausfall einzuplanen.',
  'p5.why.bundle.4': 'Damit ist kein Ausfalltermin nötig — die Maßnahme rechnet sich allein dadurch, dass sie mitläuft.',
  'p5.why.verify.1': 'An Tür {door} wurde am {date} instandgesetzt.',
  'p5.why.verify.2': 'Der Verlauf flachte zunächst ab und stieg nach {days} Tagen wieder an — die Maßnahme hat den Auslöser nicht beseitigt.',
  'p5.why.verify.3': 'Zu prüfen ist, ob die Ursache falsch bestimmt wurde oder ein Teil aus derselben Charge betroffen ist.',
  'p5.why.external.1': 'Der Verlauf an Tür {door} steigt nur an Tagen mit langen Halten in der Hauptverkehrszeit.',
  'p5.why.external.2': 'Drei Türen derselben Linie zeigen dasselbe Muster — an unterschiedlichen Einheiten.',
  'p5.why.external.3': 'Ein Tausch an dieser Tür würde daran nichts ändern. Sinnvoll ist eine Prüfung des Umlaufs, nicht der Tür.',
  'p5.slot.alreadyBooked': 'bereits geplanter Aufenthalt — Fenster erweitern',
  'p5.slot.reinspect': 'kurzfristige Nachschau',
  'p5.slot.noAction': 'keine Maßnahme an der Tür empfohlen',
  'p5.chk.roller': 'Laufrollen und Führung sichtprüfen, bei Bedarf tauschen',
  'p5.chk.rail': 'Laufschiene reinigen und auf Riefen prüfen',
  'p5.chk.belt': 'Riemenspannung prüfen',
  'p5.chk.recalibrate': 'Antrieb neu einlernen',
  'p5.chk.recycle': 'Zehn Referenzzyklen fahren und Strom aufzeichnen',
  'p5.chk.reviewOrder': 'Vorherigen Auftrag und Befund sichten',
  'p5.chk.partBatch': 'Chargennummer des verbauten Teils erfassen',
  'p5.chk.dutyReview': 'Umlauf und Haltezeiten mit dem Betrieb bewerten',
  'p5.chk.compareFleet': 'Türen gleicher Linie gegenüberstellen',
  'p5.arch.1.title': 'Merkmale je Zyklus',
  'p5.arch.1.body': 'Aus jedem Türzyklus werden Stromverlauf, Energie und Dauer je Phase extrahiert. Reine Signalverarbeitung.',
  'p5.arch.2.title': 'Trend und Prognoseband',
  'p5.arch.2.body': 'Das eigene Prognosemodell schätzt Rate und Unsicherheit. Deterministisch, versionierbar, ohne Sprachmodell.',
  'p5.arch.3.title': 'Zusammenführung',
  'p5.arch.3.body': 'Der Agent verknüpft den Trend mit Historie, Freitextnotizen, Depotbelegung und Teileverfügbarkeit.',
  'p5.arch.4.title': 'Empfehlung und Auftrag',
  'p5.arch.4.body': 'Daraus entstehen Priorisierung, Zeitfenster, Checkliste und Auftrag — und nach der Maßnahme die Wirksamkeitsprüfung.',
};

const en: Dict = {
  'app.signOut': 'Sign out',
  'app.allDemos': 'All',
  'app.language': 'Language',
  'app.back': 'Back',

  'landing.eyebrow': 'Prototype Lab',
  'landing.title': 'Door Intelligence',
  'landing.subtitle': 'Prototype Demos',
  'landing.intro': 'Working demonstrators exploring what door systems can tell us once their data is collected, aggregated and modelled. Each prototype below is driven by representative data — select one to explore.',
  'landing.demonstrators': 'Demonstrators',
  'landing.open': 'Open demo',
  'landing.comingSoon': 'Coming soon',
  'landing.footerNote': 'Representative data — not from revenue service',
  'landing.env': 'OPEN CAPACITY · LAB ENVIRONMENT',

  'login.eyebrow': 'Door Intelligence',
  'login.title': 'Sign in',
  'login.subtitle': 'Access to the prototype lab is by invitation.',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.error': 'Invalid email or password.',

  'fleet.reportingDate': 'Reporting date',
  'fleet.asOf': 'as of',
  'fleet.local': 'local',
  'fleet.title': 'Fleet',
  'fleet.withData': 'of {total} with data',
  'fleet.filter': 'Filter units…',
  'fleet.noData': 'no data',
  'fleet.ready': 'With data',
  'fleet.remaining': 'Rest of fleet',
  'fleet.noMatch': 'No unit matches “{q}”.',
  'fleet.footnote': 'Remaining units are listed for scale. Door data is only wired up for {units} in this build.',
  'fleet.collapse': 'Hide fleet menu',
  'fleet.expand': 'Show fleet menu',
  'fleet.unit': 'Unit',

  'proto.01.short': 'Operations',
  'proto.01.title': 'Door Operations Dashboard',
  'proto.01.tag': 'Fleet Overview',
  'proto.01.desc': 'Door usage heat map for a 4-car EMU. Cycle counts, misuse events, per-door maintenance forecast and health indicators.',
  'proto.02.short': 'Stop Activity',
  'proto.02.title': 'Stop Activity Analysis',
  'proto.02.tag': 'Passenger Flow',
  'proto.02.desc': 'Trip-level boarding intelligence. Per-stop door activity, platform vs trackside differential, forward/rear split and misuse flags.',
  'proto.03.short': 'Diagnostics',
  'proto.03.title': 'EMU Door Diagnostics',
  'proto.03.tag': 'Diagnostics',
  'proto.03.desc': 'Node diagnostics for maintenance engineers. Motor current waveforms, position traces and cycle counts across all 16 door nodes.',
  'proto.04.short': 'Anomaly Scoring',
  'proto.04.title': 'CBM Anomaly Scoring',
  'proto.04.tag': 'Predictive',
  'proto.04.desc': 'Per-cycle ensemble anomaly radar and 8-week fleet heatmap. Method agreement across Isolation Forest, LOF, SVM and Autoencoder.',

  'common.syntheticData': 'Synthetic data',
  'common.syntheticNote': 'Synthetic fleet — unit IDs, stop names, timings and identifiers are fictional',
  'common.confidential': '© 2026 Opencapacity · Confidential · Internal use only',
  'common.door': 'Door',
  'common.doors': 'Doors',
  'common.health': 'Health',
  'common.cycles': 'Cycles',
  'common.platform': 'Platform side',
  'common.trackside': 'Trackside',
  'common.forward': 'forward',
  'common.rear': 'rear',
  'common.good': 'Good',
  'common.monitor': 'Monitor',
  'common.action': 'Action',
  'common.nominal': 'Nominal',
  'common.serviceDue': 'Service due',
  'common.flagged': 'flagged',
  'common.days': 'd',

  'p1.fleetOverview': 'Fleet overview',
  'p1.alerts': '{n} alerts',
  'p1.draft': 'Draft concept',
  'p1.cyclesToday': 'Cycles today',
  'p1.vsAverage': 'vs average',
  'p1.misuseEvents': 'Misuse events',
  'p1.highSeverity': '{n} high severity',
  'p1.avgCycleTime': 'Avg cycle time',
  'p1.withinRange': 'Within normal range',
  'p1.nextService': 'Next service',
  'p1.flaggedDoor': '{door} flagged',
  'p1.schematic': 'Train set · overhead door usage · 4-car EMU',
  'p1.recentEvents': 'Recent events',
  'p1.today': 'Today',
  'p1.maintenanceForecast': 'Maintenance forecast',
  'p1.horizon': '30-day horizon',
  'p1.heat.1': 'Low',
  'p1.heat.2': 'Moderate',
  'p1.heat.3': 'High',
  'p1.heat.4': 'Very high',
  'p1.heat.5': 'Peak',
  'p1.misuseEvent': 'Misuse event',
  'p1.sideA': '◀ Side A · Platform side',
  'p1.sideB': 'Side B · Trackside ▶',
  'p1.usage': 'Usage',
  'p1.others': 'Others',
  'p1.serviceWithin': 'Service within 7 days recommended',
  'p1.approachingThreshold': 'Approaching wear threshold',
  'p1.slowClose': 'Extended close time detected',
  'p1.normalWear': 'Normal wear',
  'p1.withinExpected': 'All within expected range',
  'event.forcedEntry': 'Forced entry — door {door} held open past the close signal',
  'event.slowClose': 'Extended close time — door {door}: 6.8s (threshold 5s)',
  'event.cycleMilestone': 'Cycle milestone — door {door} reached 10,000 cycles',

  'p2.title': 'Stop Activity & Door Intelligence',
  'p2.trip': 'Trip',
  'p2.trip.morningPeak': 'Morning peak',
  'p2.trip.midday': 'Midday',
  'p2.trip.eveningPeak': 'Evening peak',
  'p2.trip.lateEvening': 'Late evening',
  'p2.overview': 'Trip overview — door openings per stop',
  'p2.selectStop': 'Select stop',
  'p2.stopOf': 'Stop {i} of {n}',
  'p2.arr': 'arr',
  'p2.totalOpenings': 'Total openings',
  'p2.activeDoors': 'Active doors',
  'p2.dwellTime': 'Dwell time',
  'p2.forcedEvents': 'Forced events',
  'p2.heatmapTitle': 'Door opening count — overhead view',
  'p2.perDoor': 'Per-door detail · click a column header to sort',
  'p2.openings': 'Openings',
  'p2.totalOpenTime': 'Total open time',
  'p2.flags': 'Flags',
  'p2.avg': 'avg',
  'p2.differential': 'Differential door usage · platform side',
  'p2.forwardPct': 'Forward (×1)',
  'p2.rearPct': 'Rear (×2)',
  'p2.unevenNote': 'An uneven split indicates boarding concentration at one end of each carriage.',
  'p2.activityByWagen': 'Activity by carriage',
  'p2.busiestDoor': 'Busiest door',
  'p2.fwdRear': 'Fwd / rear',
  'p2.positionSplit': 'position split',
  'p2.doorSpread': 'Door spread',
  'p2.maxVsMin': 'max vs min active',
  'p2.noActivity': 'No activity',
  'p2.healthPanel': 'Door health & misuse flags',
  'p2.allNominal': 'All doors nominal at this stop',
  'p2.requiresAttention': 'Requires attention',
  'p2.unitHealth': 'Unit health overview',
  'p2.sideL': '◀ Side L · Platform side',
  'p2.sideR': 'Side R · Trackside ▶',
  'p2.stops': 'stops',

  'p3.system': 'EMU door system',
  'p3.title': 'Door Node Diagnostics',
  'p3.nodesOnline': '16 nodes online',
  'p3.nodeSelector': 'System node selector — 16 doors, 4 carriages',
  'p3.monitorCondition': 'Monitor',
  'p3.lowUse': 'Low use',
  'p3.normal': 'Normal',
  'p3.high': 'High',
  'p3.critical': 'Critical',
  'p3.waveforms': 'Sensor waveforms',
  'p3.analysing': 'ANALYSING',
  'p3.current': 'Current (mA)',
  'p3.position': 'Position (8-bit)',
  'p3.cycleDistribution': 'Cycle distribution',
  'p3.selectedNode': 'Selected node',
  'p3.standby': 'Node standby',
  'p3.standbyText': 'Select a door node on the schematic, or from quick select, to run diagnostics and view its sensor waveforms.',
  'p3.quickSelect': 'Quick select',
  'p3.peakCurrent': 'Peak motor current',
  'p3.aboveCeiling': 'Above the {limit} mA expected ceiling.',
  'p3.ipAddress': 'IP address',
  'p3.doorId': 'Door ID',
  'p3.wagen': 'Carriage',
  'p3.side': 'Side',
  'p3.selectPrompt': 'Select a door node to run diagnostics',
  'p3.simulated': 'simulated waveforms',

  'p4.approach': 'Approach 2A · cycle scoring → fleet heatmap',
  'p4.title': 'Per-Cycle Anomaly Scoring & Fleet View',
  'p4.weeks': '8 weeks',
  'p4.step2': 'Step 2 — door detail',
  'p4.peakWeek': 'Peak week',
  'p4.avgAnomaly': 'Avg anomaly',
  'p4.eightWeekAvg': '8-week average',
  'p4.cyclesShown': 'Cycles shown',
  'p4.representative': 'representative',
  'p4.selectCycle': 'Select a cycle to inspect on the radar',
  'p4.step1': 'Step 1 — per-cycle scoring',
  'p4.radarIntro': 'Each axis is one detection method. A large, symmetrical polygon means strong cross-method agreement; an asymmetric one means the methods disagree, which is worth investigating on its own.',
  'p4.ensemble': 'Ensemble',
  'p4.review': 'Review',
  'p4.inspect': 'Inspect',
  'p4.step3': 'Step 3 — fleet heatmap',
  'p4.heatmapIntro': 'Aggregated output of per-cycle scoring. Hot rows flag doors for inspection — click any row to drill into that door.',
  'p4.pipeline': 'How it works — scoring pipeline',
  'p4.step': 'Step',
  'p4.normalClose': 'Normal close',
  'p4.obstructed': 'Obstructed',
  'p4.multiAttempt': 'Multi-attempt',
  'p4.openOnly': 'Open only',
  'p4.legend.normal': '<10% normal',
  'p4.legend.low': '10–20% low',
  'p4.legend.elevated': '20–35% elevated',
  'p4.legend.high': '35–55% high',
  'p4.legend.critical': '>55% critical',
  'p4.pipe1.title': '3-segment feature extraction',
  'p4.pipe1.body': 'Each door cycle is split into acceleration, constant-speed and deceleration phases. Current shape, energy and duration are extracted per segment.',
  'p4.pipe2.title': 'Four independent scorers',
  'p4.pipe2.body': 'Isolation Forest, LOF, One-Class SVM and Autoencoder each score every cycle independently. No single method dominates the outcome.',
  'p4.pipe3.title': 'Ensemble via rank averaging',
  'p4.pipe3.body': 'Scores are converted to ranks and averaged. The 0–100 ensemble score reflects cross-method consensus, so high agreement means high confidence.',
  'p4.pipe4.title': 'Fleet-level aggregation',
  'p4.pipe4.body': 'The share of HIGH cycles per door per week surfaces degradation trends. Hot rows flag candidates for maintenance review before failure.',

  // Prototype 05 — maintenance & action recommendations
  'proto.05.short': 'Maintenance',
  'proto.05.title': 'Maintenance & action recommendations',
  'proto.05.tag': 'Servicing',
  'proto.05.desc': 'Per-door condition trend with a forecast band, cycles over any chosen period, and the action recommendations derived from them — service window, checklist and work order.',
  'p5.approach': 'Servicing',
  'p5.title': 'Maintenance & action recommendations',
  'p5.illustrative': 'Illustrative',
  'p5.paletteProvisional': 'palette provisional',
  'p5.scope': 'The statistics give the condition trend — deterministic, auditable, reproducible. They do not predict a failure date: the forecast band widens with the horizon while the direction stays reliable. The recommendation sits on top of that and is deliberately opportunistic — it bundles the work into a window the unit is already booked into.',
  'p5.triage.flags': 'Statistical flags',
  'p5.triage.flags.sub': 'raised this week',
  'p5.triage.actionable': 'Worth acting on',
  'p5.triage.actionable.sub': 'prioritised by the agent',
  'p5.triage.suppressed': 'Suppressed',
  'p5.triage.suppressed.sub': 'classified as known-benign',
  'p5.det.label': 'Deterministic',
  'p5.det.title': 'Cycles & condition per door',
  'p5.det.intro': 'Plain arithmetic over count and current data. No model judgement, no language model — the same input always gives the same output.',
  'period.label': 'Period',
  'period.day': 'Per day',
  'period.week': 'Per week',
  'period.month': 'Per month',
  'period.range': 'From–to',
  'period.from': 'from',
  'period.to': 'to',
  'period.cycles': 'Cycles',
  'p3.representative': 'Representative cycle · current condition',
  'p3.bandNote': 'Reference band from {n} further cycles on this door in the period ({period}).',
  'p3.vsOwnBand': 'Against its own band',
  'p3.insideBand': 'Within its own normal spread',
  'p3.bandHigh': 'At the top of its own spread',
  'p3.bandLow': 'At the bottom of its own spread',
  'p3.bandPercentile': '{pct}th percentile of {n} reference cycles',
  'p3.bandSelfOnly': 'Compared against this door only — not against other doors.',
  'p3.cyclesLifetime': 'Cycles, lifetime',
  'p3.cyclesPeriod': 'Cycles in period',
  'period.note': 'Door figures and the total reflect the selected period ({days} days).',
  'p5.table.door': 'Door',
  'p5.table.cycles': 'Cycles',
  'p5.table.current': 'Peak current',
  'p5.table.health': 'Condition',
  'p5.table.note': 'Cycles summed over the selected period ({period}). Sorted by condition, worst door first.',
  'p5.trend.title': 'Condition trend & forecast band',
  'p5.trend.observed': 'Observed',
  'p5.trend.projected': 'Projected',
  'p5.trend.band': 'Forecast band',
  'p5.trend.mid': 'Mid',
  'p5.trend.advisory': 'Advisory',
  'p5.trend.today': 'today',
  'p5.trend.daysOut': 'days',
  'p5.trend.rate': 'Rate of change',
  'p5.trend.rateSub': 'A per week',
  'p5.trend.spread': 'Band width',
  'p5.trend.spreadSub': 'A at +{days} days',
  'p5.trend.advisoryIn': 'Advisory in',
  'p5.trend.withinHorizon': 'within the horizon',
  'p5.trend.beyondHorizon': 'beyond the horizon',
  'p5.trend.caveat': 'The band widens with the horizon — that is the point. It states how far the trend can be trusted, not when the door will fail.',
  'p5.agent.label': 'Orchestration',
  'p5.agent.title': 'Action recommendations (AI agent)',
  'p5.agent.intro': 'The agent does not improve on the statistics — it joins things up. It connects trend and forecast to maintenance history, free-text notes from the last inspection, depot scheduling and parts availability, and turns them into a recommendation, a window, a checklist and a work order.',
  'p5.kind.bundle': 'Bundling',
  'p5.kind.verify': 'Effectiveness check',
  'p5.kind.external': 'External factor',
  'p5.prio.high': 'High',
  'p5.prio.medium': 'Medium',
  'p5.prio.low': 'Low',
  'p5.rec.evidence': 'Evidence used',
  'p5.rec.why': 'Why this recommendation?',
  'p5.rec.hideWhy': 'Hide reasoning',
  'p5.rec.slot': 'Proposed window',
  'p5.rec.order': 'Order',
  'p5.rec.checklist': 'Checklist',
  'p5.rec.minutes': 'min',
  'p5.rec.bundle.title': 'Add door {door} to the depot visit already booked for {date}.',
  'p5.rec.verify.title': 'The repair on door {door} did not hold — trend resumed after {days} days.',
  'p5.rec.external.title': 'Wear on door {door} tracks the duty pattern, not the door.',
  'p5.ev.rate': 'Rate',
  'p5.ev.current': 'Peak current',
  'p5.ev.notes': 'Inspection note',
  'p5.ev.slot': 'Depot window',
  'p5.ev.parts': 'Spare part',
  'p5.ev.intervention': 'Last intervention',
  'p5.ev.postRepair': 'After repair',
  'p5.ev.correlation': 'Correlation',
  'p5.ev.duty': 'Duty pattern',
  'p5.ev.peers': 'Peer fleet',
  'p5.src.stats': 'statistics',
  'p5.src.notes': 'inspection',
  'p5.src.depot': 'depot plan',
  'p5.src.cmms': 'CMMS',
  'p5.src.timetable': 'duty roster',
  'p5.note.roller': 'rollers slightly rough, left in service',
  'p5.parts.inStock': 'in stock',
  'p5.post.resumed': 'rise resumed after 11 days',
  'p5.corr.dwell': 'dwell time & boarding count',
  'p5.duty.peak': 'peak hour, long dwell',
  'p5.peers.sameRoute': '3 doors on the same route alike',
  'p5.why.bundle.1': 'Door {door} has been climbing steadily at {rate} A per week for weeks — a trend, not a single outlier.',
  'p5.why.bundle.2': 'The last inspection note describes rough running with no action taken. That matches the measured curve.',
  'p5.why.bundle.3': 'The unit is in depot {depot} on {date} anyway. The window can be extended without scheduling additional downtime.',
  'p5.why.bundle.4': 'So no failure date is needed — the work pays for itself simply by riding along.',
  'p5.why.verify.1': 'Door {door} was repaired on {date}.',
  'p5.why.verify.2': 'The curve flattened at first and resumed after {days} days — the intervention did not remove the cause.',
  'p5.why.verify.3': 'Worth checking whether the root cause was misdiagnosed, or whether a part from the same batch is involved.',
  'p5.why.external.1': 'The curve on door {door} only rises on days with long peak-hour dwells.',
  'p5.why.external.2': 'Three doors on the same route show the same pattern — across different units.',
  'p5.why.external.3': 'Replacing parts on this door would change nothing. What is worth reviewing is the duty, not the door.',
  'p5.slot.alreadyBooked': 'already-booked visit — extend the window',
  'p5.slot.reinspect': 'short re-inspection',
  'p5.slot.noAction': 'no work recommended on the door',
  'p5.chk.roller': 'Inspect rollers and guide, replace if required',
  'p5.chk.rail': 'Clean the running rail and check for scoring',
  'p5.chk.belt': 'Check belt tension',
  'p5.chk.recalibrate': 'Re-teach the drive',
  'p5.chk.recycle': 'Run ten reference cycles and log current',
  'p5.chk.reviewOrder': 'Review the previous order and findings',
  'p5.chk.partBatch': 'Record the batch number of the fitted part',
  'p5.chk.dutyReview': 'Review duty and dwell times with operations',
  'p5.chk.compareFleet': 'Compare against doors on the same route',
  'p5.arch.1.title': 'Per-cycle features',
  'p5.arch.1.body': 'Current profile, energy and duration are extracted per phase from every door cycle. Pure signal processing.',
  'p5.arch.2.title': 'Trend and forecast band',
  'p5.arch.2.body': 'Our own forecasting model estimates rate and uncertainty. Deterministic, versionable, no language model.',
  'p5.arch.3.title': 'Joining up',
  'p5.arch.3.body': 'The agent connects the trend to history, free-text notes, depot scheduling and parts availability.',
  'p5.arch.4.title': 'Recommendation and order',
  'p5.arch.4.body': 'Out of that come priority, window, checklist and work order — and, after the work, the effectiveness check.',
};

const fr: Dict = {
  'app.signOut': 'Se déconnecter',
  'app.allDemos': 'Tout',
  'app.language': 'Langue',
  'app.back': 'Retour',

  'landing.eyebrow': 'Laboratoire de prototypes',
  'landing.title': 'Intelligence des portes',
  'landing.subtitle': 'Démonstrateurs',
  'landing.intro': 'Démonstrateurs fonctionnels explorant ce que les systèmes de portes peuvent révéler une fois leurs données collectées, agrégées et modélisées. Chaque prototype repose sur des données représentatives — choisissez-en un.',
  'landing.demonstrators': 'Démonstrateurs',
  'landing.open': 'Ouvrir la démo',
  'landing.comingSoon': 'Bientôt disponible',
  'landing.footerNote': 'Données représentatives — hors service commercial',
  'landing.env': 'OPEN CAPACITY · ENVIRONNEMENT DE LABORATOIRE',

  'login.eyebrow': 'Intelligence des portes',
  'login.title': 'Connexion',
  'login.subtitle': "L'accès au laboratoire de prototypes se fait sur invitation.",
  'login.email': 'E-mail',
  'login.password': 'Mot de passe',
  'login.submit': 'Se connecter',
  'login.error': 'Adresse e-mail ou mot de passe invalide.',

  'fleet.reportingDate': "Date d'analyse",
  'fleet.asOf': 'au',
  'fleet.local': 'heure locale',
  'fleet.title': 'Flotte',
  'fleet.withData': 'sur {total} avec données',
  'fleet.filter': 'Filtrer les rames…',
  'fleet.noData': 'aucune donnée',
  'fleet.ready': 'Avec données',
  'fleet.remaining': 'Reste de la flotte',
  'fleet.noMatch': 'Aucune rame ne correspond à « {q} ».',
  'fleet.footnote': "Les autres rames sont listées pour illustrer la taille de la flotte. Les données de portes ne sont disponibles que pour {units} dans cette version.",
  'fleet.collapse': 'Masquer le menu flotte',
  'fleet.expand': 'Afficher le menu flotte',
  'fleet.unit': 'Rame',

  'proto.01.short': 'Exploitation',
  'proto.01.title': "Tableau de bord d'exploitation",
  'proto.01.tag': 'Vue flotte',
  'proto.01.desc': "Carte thermique d'utilisation des portes d'une rame automotrice à quatre caisses. Cycles, événements d'usage abusif, prévision de maintenance et indicateurs d'état par porte.",
  'proto.02.short': 'Activité arrêts',
  'proto.02.title': "Analyse de l'activité aux arrêts",
  'proto.02.tag': 'Flux voyageurs',
  'proto.02.desc': "Intelligence d'embarquement au niveau du trajet. Activité des portes par arrêt, différentiel quai/voie, répartition avant/arrière et signalements d'usage abusif.",
  'proto.03.short': 'Diagnostic',
  'proto.03.title': 'Diagnostic des nœuds de porte',
  'proto.03.tag': 'Diagnostic',
  'proto.03.desc': "Diagnostic des nœuds pour la maintenance. Courbes de courant moteur, relevés de position et comptages de cycles sur les 16 nœuds de porte.",
  'proto.04.short': 'Anomalies',
  'proto.04.title': "Notation d'anomalies CBM",
  'proto.04.tag': 'Prédictif',
  'proto.04.desc': "Radar d'anomalies par cycle et carte thermique de flotte sur huit semaines. Concordance entre Isolation Forest, LOF, SVM et autoencodeur.",

  'common.syntheticData': 'Données synthétiques',
  'common.syntheticNote': 'Flotte synthétique — rames, arrêts, horaires et identifiants sont fictifs',
  'common.confidential': '© 2026 Opencapacity · Confidentiel · Usage interne uniquement',
  'common.door': 'Porte',
  'common.doors': 'Portes',
  'common.health': 'État',
  'common.cycles': 'Cycles',
  'common.platform': 'Côté quai',
  'common.trackside': 'Côté voie',
  'common.forward': 'avant',
  'common.rear': 'arrière',
  'common.good': 'Bon',
  'common.monitor': 'À surveiller',
  'common.action': 'À traiter',
  'common.nominal': 'Nominal',
  'common.serviceDue': 'Maintenance due',
  'common.flagged': 'signalées',
  'common.days': 'j',

  'p1.fleetOverview': 'Vue flotte',
  'p1.alerts': '{n} alertes',
  'p1.draft': 'Concept',
  'p1.cyclesToday': "Cycles aujourd'hui",
  'p1.vsAverage': 'par rapport à la moyenne',
  'p1.misuseEvents': "Événements d'usage abusif",
  'p1.highSeverity': '{n} de gravité élevée',
  'p1.avgCycleTime': 'Durée moyenne de cycle',
  'p1.withinRange': 'Dans la plage normale',
  'p1.nextService': 'Prochaine maintenance',
  'p1.flaggedDoor': '{door} signalée',
  'p1.schematic': 'Rame · utilisation des portes vue de dessus · automotrice à quatre caisses',
  'p1.recentEvents': 'Événements récents',
  'p1.today': "Aujourd'hui",
  'p1.maintenanceForecast': 'Prévision de maintenance',
  'p1.horizon': 'horizon 30 jours',
  'p1.heat.1': 'Faible',
  'p1.heat.2': 'Modérée',
  'p1.heat.3': 'Élevée',
  'p1.heat.4': 'Très élevée',
  'p1.heat.5': 'Maximale',
  'p1.misuseEvent': "Événement d'usage abusif",
  'p1.sideA': '◀ Côté A · côté quai',
  'p1.sideB': 'Côté B · côté voie ▶',
  'p1.usage': 'Utilisation',
  'p1.others': 'Autres',
  'p1.serviceWithin': 'Maintenance recommandée sous 7 jours',
  'p1.approachingThreshold': "Approche du seuil d'usure",
  'p1.slowClose': 'Temps de fermeture allongé détecté',
  'p1.normalWear': 'Usure normale',
  'p1.withinExpected': 'Toutes dans la plage attendue',
  'event.forcedEntry': "Ouverture forcée — porte {door} maintenue ouverte après le signal de fermeture",
  'event.slowClose': 'Temps de fermeture allongé — porte {door} : 6,8 s (seuil 5 s)',
  'event.cycleMilestone': 'Jalon de cycles — la porte {door} a atteint 10 000 cycles',

  'p2.title': "Activité aux arrêts & intelligence des portes",
  'p2.trip': 'Trajet',
  'p2.trip.morningPeak': 'Pointe du matin',
  'p2.trip.midday': 'Milieu de journée',
  'p2.trip.eveningPeak': 'Pointe du soir',
  'p2.trip.lateEvening': 'Fin de soirée',
  'p2.overview': "Vue du trajet — ouvertures de portes par arrêt",
  'p2.selectStop': 'Choisir un arrêt',
  'p2.stopOf': 'Arrêt {i} sur {n}',
  'p2.arr': 'arr',
  'p2.totalOpenings': 'Ouvertures totales',
  'p2.activeDoors': 'Portes actives',
  'p2.dwellTime': "Temps d'arrêt",
  'p2.forcedEvents': 'Ouvertures forcées',
  'p2.heatmapTitle': "Nombre d'ouvertures — vue de dessus",
  'p2.perDoor': "Détail par porte · cliquer sur un en-tête pour trier",
  'p2.openings': 'Ouvertures',
  'p2.totalOpenTime': "Temps d'ouverture total",
  'p2.flags': 'Signalements',
  'p2.avg': 'moy',
  'p2.differential': 'Utilisation différentielle · côté quai',
  'p2.forwardPct': 'Avant (×1)',
  'p2.rearPct': 'Arrière (×2)',
  'p2.unevenNote': "Une répartition inégale indique une concentration de l'embarquement à une extrémité de chaque caisse.",
  'p2.activityByWagen': 'Activité par caisse',
  'p2.busiestDoor': 'Porte la plus sollicitée',
  'p2.fwdRear': 'Avant / arrière',
  'p2.positionSplit': 'répartition par position',
  'p2.doorSpread': 'Écart entre portes',
  'p2.maxVsMin': 'max. contre min. active',
  'p2.noActivity': 'Aucune activité',
  'p2.healthPanel': "État des portes & signalements d'usage abusif",
  'p2.allNominal': 'Toutes les portes sont nominales à cet arrêt',
  'p2.requiresAttention': 'Nécessite une intervention',
  'p2.unitHealth': "Vue d'ensemble de l'état de la rame",
  'p2.sideL': '◀ Côté L · côté quai',
  'p2.sideR': 'Côté R · côté voie ▶',
  'p2.stops': 'arrêts',

  'p3.system': 'Système de portes',
  'p3.title': 'Diagnostic des nœuds de porte',
  'p3.nodesOnline': '16 nœuds en ligne',
  'p3.nodeSelector': 'Sélection des nœuds — 16 portes, 4 caisses',
  'p3.monitorCondition': 'À surveiller',
  'p3.lowUse': 'Faible usage',
  'p3.normal': 'Normal',
  'p3.high': 'Élevé',
  'p3.critical': 'Critique',
  'p3.waveforms': 'Signaux capteurs',
  'p3.analysing': 'ANALYSE EN COURS',
  'p3.current': 'Courant (mA)',
  'p3.position': 'Position (8 bits)',
  'p3.cycleDistribution': 'Répartition des cycles',
  'p3.selectedNode': 'Nœud sélectionné',
  'p3.standby': 'Nœud en attente',
  'p3.standbyText': "Sélectionnez un nœud de porte sur le schéma ou dans la sélection rapide pour lancer le diagnostic et afficher ses signaux capteurs.",
  'p3.quickSelect': 'Sélection rapide',
  'p3.peakCurrent': 'Courant moteur maximal',
  'p3.aboveCeiling': 'Au-dessus du plafond attendu de {limit} mA.',
  'p3.ipAddress': 'Adresse IP',
  'p3.doorId': 'ID de porte',
  'p3.wagen': 'Caisse',
  'p3.side': 'Côté',
  'p3.selectPrompt': 'Sélectionner un nœud de porte pour lancer le diagnostic',
  'p3.simulated': 'signaux simulés',

  'p4.approach': 'Approche 2A · notation par cycle → carte thermique de flotte',
  'p4.title': "Notation d'anomalies par cycle & vue flotte",
  'p4.weeks': '8 semaines',
  'p4.step2': 'Étape 2 — détail de la porte',
  'p4.peakWeek': 'Semaine de pointe',
  'p4.avgAnomaly': 'Anomalie moyenne',
  'p4.eightWeekAvg': 'moyenne sur 8 semaines',
  'p4.cyclesShown': 'Cycles affichés',
  'p4.representative': 'représentatifs',
  'p4.selectCycle': 'Choisir un cycle à examiner sur le radar',
  'p4.step1': 'Étape 1 — notation par cycle',
  'p4.radarIntro': "Chaque axe correspond à une méthode de détection. Un polygone grand et symétrique indique une forte concordance entre méthodes ; un polygone asymétrique signale un désaccord, ce qui mérite en soi une investigation.",
  'p4.ensemble': 'Ensemble',
  'p4.review': 'À revoir',
  'p4.inspect': 'À inspecter',
  'p4.step3': 'Étape 3 — carte thermique de flotte',
  'p4.heatmapIntro': "Résultat agrégé de la notation par cycle. Les lignes chaudes signalent les portes à inspecter — cliquez sur une ligne pour l'explorer.",
  'p4.pipeline': 'Fonctionnement — chaîne de notation',
  'p4.step': 'Étape',
  'p4.normalClose': 'Fermeture normale',
  'p4.obstructed': 'Obstruée',
  'p4.multiAttempt': 'Tentatives multiples',
  'p4.openOnly': 'Ouverture seule',
  'p4.legend.normal': '<10 % normal',
  'p4.legend.low': '10–20 % faible',
  'p4.legend.elevated': '20–35 % élevé',
  'p4.legend.high': '35–55 % fort',
  'p4.legend.critical': '>55 % critique',
  'p4.pipe1.title': 'Extraction de caractéristiques en trois segments',
  'p4.pipe1.body': "Chaque cycle de porte est découpé en phases d'accélération, de vitesse constante et de décélération. Forme du courant, énergie et durée sont extraites par segment.",
  'p4.pipe2.title': 'Quatre détecteurs indépendants',
  'p4.pipe2.body': "Isolation Forest, LOF, SVM à une classe et autoencodeur notent chaque cycle indépendamment. Aucune méthode ne domine le résultat.",
  'p4.pipe3.title': 'Ensemble par moyenne des rangs',
  'p4.pipe3.body': "Les scores sont convertis en rangs puis moyennés. Le score d'ensemble de 0 à 100 reflète le consensus entre méthodes.",
  'p4.pipe4.title': 'Agrégation au niveau de la flotte',
  'p4.pipe4.body': "La part de cycles anormaux par porte et par semaine révèle les tendances de dégradation. Les lignes chaudes signalent des candidats à la maintenance avant panne.",

  // Prototype 05 — maintenance & action recommendations
  'proto.05.short': 'Maintenance',
  'proto.05.title': "Maintenance & recommandations d'action",
  'proto.05.tag': 'Entretien',
  'proto.05.desc': "Tendance d'état par porte avec bande de prévision, cycles sur toute période choisie, et les recommandations qui en découlent — créneau, liste de contrôle et ordre de travail.",
  'p5.approach': 'Entretien',
  'p5.title': "Maintenance & recommandations d'action",
  'p5.illustrative': 'Illustratif',
  'p5.paletteProvisional': 'palette provisoire',
  'p5.scope': "La statistique donne la tendance d'état — déterministe, auditable, reproductible. Elle ne prédit pas de date de panne : la bande de prévision s'élargit avec l'horizon tandis que la direction reste fiable. La recommandation s'appuie dessus et reste volontairement opportuniste — elle regroupe les travaux dans un créneau déjà prévu.",
  'p5.triage.flags': 'Signalements statistiques',
  'p5.triage.flags.sub': 'relevés cette semaine',
  'p5.triage.actionable': 'À traiter',
  'p5.triage.actionable.sub': "priorisés par l'agent",
  'p5.triage.suppressed': 'Supprimés',
  'p5.triage.suppressed.sub': 'jugés bénins et connus',
  'p5.det.label': 'Déterministe',
  'p5.det.title': 'Cycles & état par porte',
  'p5.det.intro': 'Arithmétique simple sur les comptages et les courants. Aucun jugement de modèle, aucun modèle de langage — même entrée, même sortie.',
  'period.label': 'Période',
  'period.day': 'Par jour',
  'period.week': 'Par semaine',
  'period.month': 'Par mois',
  'period.range': 'De–à',
  'period.from': 'du',
  'period.to': 'au',
  'period.cycles': 'Cycles',
  'p3.representative': 'Cycle représentatif · état actuel',
  'p3.bandNote': "Bande de référence issue de {n} autres cycles de cette porte sur la période ({period}).",
  'p3.vsOwnBand': 'Face à sa propre bande',
  'p3.insideBand': 'Dans sa dispersion habituelle',
  'p3.bandHigh': 'En haut de sa propre dispersion',
  'p3.bandLow': 'En bas de sa propre dispersion',
  'p3.bandPercentile': '{pct}e centile de {n} cycles de référence',
  'p3.bandSelfOnly': "Comparaison avec cette porte uniquement — pas avec les autres.",
  'p3.cyclesLifetime': 'Cycles, total',
  'p3.cyclesPeriod': 'Cycles sur la période',
  'period.note': "Les valeurs par porte et le total portent sur la période choisie ({days} jours).",
  'p5.table.door': 'Porte',
  'p5.table.cycles': 'Cycles',
  'p5.table.current': 'Courant crête',
  'p5.table.health': 'État',
  'p5.table.note': 'Cycles cumulés sur la période choisie ({period}). Trié par état, porte la plus dégradée en premier.',
  'p5.trend.title': "Tendance d'état & bande de prévision",
  'p5.trend.observed': 'Mesuré',
  'p5.trend.projected': 'Projeté',
  'p5.trend.band': 'Bande de prévision',
  'p5.trend.mid': 'Moyenne',
  'p5.trend.advisory': 'Seuil de contrôle',
  'p5.trend.today': "aujourd'hui",
  'p5.trend.daysOut': 'jours',
  'p5.trend.rate': "Taux d'évolution",
  'p5.trend.rateSub': 'A par semaine',
  'p5.trend.spread': 'Largeur de bande',
  'p5.trend.spreadSub': 'A à +{days} jours',
  'p5.trend.advisoryIn': 'Seuil atteint dans',
  'p5.trend.withinHorizon': "dans l'horizon",
  'p5.trend.beyondHorizon': "au-delà de l'horizon",
  'p5.trend.caveat': "La bande s'élargit avec l'horizon — c'est précisément le propos. Elle indique jusqu'où la tendance est fiable, pas quand la porte tombera en panne.",
  'p5.agent.label': 'Orchestration',
  'p5.agent.title': "Recommandations d'action (agent IA)",
  'p5.agent.intro': "L'agent n'améliore pas la statistique — il relie. Il rapproche tendance et prévision de l'historique de maintenance, des notes libres de la dernière inspection, du planning de dépôt et de la disponibilité des pièces, et en tire une recommandation, un créneau, une liste de contrôle et un ordre de travail.",
  'p5.kind.bundle': 'Regroupement',
  'p5.kind.verify': "Contrôle d'efficacité",
  'p5.kind.external': 'Facteur externe',
  'p5.prio.high': 'Élevée',
  'p5.prio.medium': 'Moyenne',
  'p5.prio.low': 'Faible',
  'p5.rec.evidence': 'Éléments utilisés',
  'p5.rec.why': 'Pourquoi cette recommandation ?',
  'p5.rec.hideWhy': 'Masquer le raisonnement',
  'p5.rec.slot': 'Créneau proposé',
  'p5.rec.order': 'Ordre',
  'p5.rec.checklist': 'Liste de contrôle',
  'p5.rec.minutes': 'min',
  'p5.rec.bundle.title': 'Intégrer la porte {door} au passage en dépôt déjà prévu le {date}.',
  'p5.rec.verify.title': "La réparation de la porte {door} n'a pas tenu — la tendance repart après {days} jours.",
  'p5.rec.external.title': "L'usure de la porte {door} suit le profil d'exploitation, pas la porte.",
  'p5.ev.rate': 'Taux',
  'p5.ev.current': 'Courant crête',
  'p5.ev.notes': "Note d'inspection",
  'p5.ev.slot': 'Créneau dépôt',
  'p5.ev.parts': 'Pièce',
  'p5.ev.intervention': 'Dernière intervention',
  'p5.ev.postRepair': 'Après réparation',
  'p5.ev.correlation': 'Corrélation',
  'p5.ev.duty': "Profil d'exploitation",
  'p5.ev.peers': 'Flotte comparable',
  'p5.src.stats': 'statistique',
  'p5.src.notes': 'inspection',
  'p5.src.depot': 'planning dépôt',
  'p5.src.cmms': 'GMAO',
  'p5.src.timetable': 'roulement',
  'p5.note.roller': 'galets légèrement rugueux, laissés en service',
  'p5.parts.inStock': 'en stock',
  'p5.post.resumed': 'hausse repartie après 11 jours',
  'p5.corr.dwell': "temps d'arrêt & montées",
  'p5.duty.peak': 'heure de pointe, arrêt long',
  'p5.peers.sameRoute': '3 portes de la même ligne id.',
  'p5.why.bundle.1': 'La porte {door} monte régulièrement de {rate} A par semaine depuis des semaines — une tendance, pas un écart isolé.',
  'p5.why.bundle.2': "La dernière note d'inspection décrit un fonctionnement rugueux sans action menée. Cela concorde avec la courbe mesurée.",
  'p5.why.bundle.3': "L'unité est de toute façon au dépôt {depot} le {date}. Le créneau peut être élargi sans immobilisation supplémentaire.",
  'p5.why.bundle.4': "Aucune date de panne n'est donc nécessaire — l'intervention se justifie par le seul fait qu'elle s'ajoute à un passage prévu.",
  'p5.why.verify.1': 'La porte {door} a été réparée le {date}.',
  'p5.why.verify.2': "La courbe s'est d'abord aplatie puis est repartie après {days} jours — l'intervention n'a pas supprimé la cause.",
  'p5.why.verify.3': 'Il faut vérifier si la cause a été mal identifiée ou si une pièce du même lot est en cause.',
  'p5.why.external.1': 'La courbe de la porte {door} ne monte que les jours à arrêts longs en heure de pointe.',
  'p5.why.external.2': 'Trois portes de la même ligne montrent le même motif — sur des unités différentes.',
  'p5.why.external.3': "Un remplacement sur cette porte n'y changerait rien. C'est le roulement qu'il faut examiner, pas la porte.",
  'p5.slot.alreadyBooked': 'passage déjà prévu — élargir le créneau',
  'p5.slot.reinspect': 'contrôle rapproché',
  'p5.slot.noAction': 'aucune intervention recommandée sur la porte',
  'p5.chk.roller': 'Contrôler galets et guidage, remplacer si besoin',
  'p5.chk.rail': 'Nettoyer le rail et vérifier les rayures',
  'p5.chk.belt': 'Vérifier la tension de la courroie',
  'p5.chk.recalibrate': "Réapprendre l'entraînement",
  'p5.chk.recycle': 'Effectuer dix cycles de référence et enregistrer le courant',
  'p5.chk.reviewOrder': "Revoir l'ordre précédent et le constat",
  'p5.chk.partBatch': 'Relever le numéro de lot de la pièce posée',
  'p5.chk.dutyReview': "Évaluer roulement et temps d'arrêt avec l'exploitation",
  'p5.chk.compareFleet': 'Comparer aux portes de la même ligne',
  'p5.arch.1.title': 'Caractéristiques par cycle',
  'p5.arch.1.body': 'Profil de courant, énergie et durée sont extraits par phase de chaque cycle. Traitement du signal pur.',
  'p5.arch.2.title': 'Tendance et bande de prévision',
  'p5.arch.2.body': "Notre propre modèle estime le taux et l'incertitude. Déterministe, versionnable, sans modèle de langage.",
  'p5.arch.3.title': 'Mise en relation',
  'p5.arch.3.body': "L'agent relie la tendance à l'historique, aux notes libres, au planning de dépôt et aux pièces disponibles.",
  'p5.arch.4.title': 'Recommandation et ordre',
  'p5.arch.4.body': "En découlent priorité, créneau, liste de contrôle et ordre de travail — puis, après les travaux, le contrôle d'efficacité.",
};

const DICTS: Record<Lang, Dict> = { de, en, fr };

type I18nContextType = {
  lang: Lang;
  setLang: (l: Lang) => void;
  locale: string;
  /** Translate a key, with optional {placeholder} substitution. */
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextType | null>(null);

function readStored(): Lang {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'de' || v === 'en' || v === 'fr') return v;
  } catch {
    // Storage unavailable (private mode, embedded webview) — fall through.
  }
  return 'de';
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(readStored);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { window.localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<I18nContextType>(() => {
    const dict = DICTS[lang];
    return {
      lang,
      setLang,
      locale: LANGUAGES.find(l => l.code === lang)?.locale ?? 'de-DE',
      t: (key, vars) => {
        let s = dict[key] ?? DICTS.de[key] ?? key;
        if (vars) {
          Object.entries(vars).forEach(([k, v]) => {
            s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
          });
        }
        return s;
      },
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextType => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside an I18nProvider');
  return ctx;
};
